/**
 * Contest on a Priority List (migration 969): "Who did not agree".
 *
 * A Participant records that they did not agree with a ratified or published
 * Priority List, with a reason. Who agreed is on the list (ratified_by,
 * published_by); this is who did not. A contest changes nothing about the list.
 *
 * Controls are drawn only for a person who may use them (build contract §2.8):
 * "Contest" for a signed-in member of the list's workspace with no open contest
 * on it, while the list is ratified or published; "Withdraw" for the contester,
 * on their own open contest. The acts stay the authority and refuse the rest.
 */
import { useState } from 'react';
import { Modal, TextArea, Field } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import type { PriorityListContestsState } from '@/hooks/usePriorityListContests';
import { contestPriorityList, withdrawContest, type ContestView } from '@/lib/contestService';

function contesterName(c: ContestView, nameOf: (id: string | null | undefined) => string): string {
  const fromView = c.contested_by_name?.trim();
  if (fromView) return fromView;
  const fromMembers = nameOf(c.contested_by);
  return fromMembers && fromMembers !== 'Someone' && fromMembers !== 'nobody' ? fromMembers : 'A member';
}

function when(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The "Who did not agree" section: each contest, who, when, the reason; Withdraw for the contester. */
export function WhoDidNotAgreeSection({
  state,
  userId,
}: {
  state: PriorityListContestsState;
  userId: string | null | undefined;
}) {
  const [withdrawing, setWithdrawing] = useState<ContestView | null>(null);
  const { contests, openContests, nameOf, error, reload } = state;
  const withdrawn = (contests ?? []).filter((c) => !c.is_open);

  return (
    <div id="who-did-not-agree" className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
      <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">
        Who did not agree ({openContests.length})
      </h2>
      <p className="text-sm text-[var(--text-ter)] mb-4">
        A contest records that a person did not agree with this list, and why. It changes nothing about the list.
      </p>

      {error && <div className="mb-3 text-sm text-[var(--coral)]">{error}</div>}

      {contests !== null && openContests.length === 0 && (
        <p className="text-sm text-[var(--text-ter)]">No one has contested this list.</p>
      )}

      <div className="space-y-3">
        {openContests.map((c) => (
          <div key={c.contest_id} className="p-3 bg-[var(--surface)] border border-[var(--rule)]">
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="text-sm font-medium text-[var(--text-pri)]">
                {contesterName(c, nameOf)}
                {c.contested_by === userId && <span className="ml-1 font-normal text-[var(--text-ter)]">(you)</span>}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-xs text-[var(--text-ter)]">{when(c.contested_at)}</span>
                {c.contested_by === userId && (
                  <Button variant="ghost" size="sm" onClick={() => setWithdrawing(c)}>
                    Withdraw
                  </Button>
                )}
              </span>
            </div>
            <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap">{c.reason}</p>
          </div>
        ))}
      </div>

      {withdrawn.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">
            Withdrawn ({withdrawn.length})
          </p>
          <div className="space-y-2">
            {withdrawn.map((c) => (
              <div key={c.contest_id} className="p-3 border border-[var(--rule)] text-sm text-[var(--text-ter)]">
                <span className="text-[var(--text-sec)]">{contesterName(c, nameOf)}</span> contested on {when(c.contested_at)} and
                withdrew on {when(c.withdrawn_at)}: {c.withdrawn_basis}
                <p className="mt-1 whitespace-pre-wrap">Reason given: {c.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {withdrawing && (
        <WithdrawContestModal
          contest={withdrawing}
          onClose={() => setWithdrawing(null)}
          onDone={async () => {
            setWithdrawing(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}

/** The reason a person gives when they contest a list. */
export function ContestListModal({
  listId,
  listName,
  onClose,
  onDone,
}: {
  listId: string;
  listName: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    const res = await contestPriorityList(listId, reason.trim());
    setSaving(false);
    if (!res.ok) {
      setErr(res.error);
      return;
    }
    await onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Contest this list"
      description={`Record that you did not agree with "${listName}", and why. The list stays as it is; your contest is shown beside who agreed.`}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="cta" size="sm" disabled={!reason.trim() || saving} onClick={() => void submit()}>
            Contest
          </Button>
        </div>
      }
    >
      <Field label="Reason" required hint="What you did not agree with: an item's place, the cut line, what was left out.">
        <TextArea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {err && <div className="text-sm text-[var(--coral)] mt-2">{err}</div>}
    </Modal>
  );
}

function WithdrawContestModal({
  contest,
  onClose,
  onDone,
}: {
  contest: ContestView;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!contest.contest_id) return;
    setSaving(true);
    const res = await withdrawContest(contest.contest_id, basis.trim());
    setSaving(false);
    if (!res.ok) {
      setErr(res.error);
      return;
    }
    await onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Withdraw your contest"
      description="Your contest stays on the record as withdrawn, with your reason for taking it back."
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="cta" size="sm" disabled={!basis.trim() || saving} onClick={() => void submit()}>
            Withdraw
          </Button>
        </div>
      }
    >
      <Field label="Basis" required hint="What changed.">
        <TextArea rows={3} value={basis} onChange={(e) => setBasis(e.target.value)} />
      </Field>
      {err && <div className="text-sm text-[var(--coral)] mt-2">{err}</div>}
    </Modal>
  );
}
